import { useState } from "react";
import {
  Car,
  Footprints,
  Bike,
  Train,
  Bus as BusIcon,
  ChevronDown,
  MoreHorizontal,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger, Button } from "@tabipla/ui";
import type { TravelMode } from "@tabipla/shared";

interface TravelTimeIndicatorProps {
  durationMinutes: number | null;
  distanceKm: number | null;
  mode: TravelMode | null;
  isEstimate?: boolean;
  onModeChange?: (mode: TravelMode) => void;
  /** Show placeholder when travel info is not available */
  showPlaceholder?: boolean;
}

const MODE_ICONS: Record<TravelMode, React.ReactNode> = {
  driving: <Car className="h-3.5 w-3.5" />,
  walking: <Footprints className="h-3.5 w-3.5" />,
  cycling: <Bike className="h-3.5 w-3.5" />,
  transit: <Train className="h-3.5 w-3.5" />,
  bus: <BusIcon className="h-3.5 w-3.5" />,
};

const MODE_COLORS: Record<TravelMode, string> = {
  driving: "text-blue-600",
  walking: "text-green-600",
  cycling: "text-orange-600",
  transit: "text-purple-600",
  bus: "text-teal-600",
};

const MODE_LABELS: Record<TravelMode, string> = {
  driving: "車",
  walking: "徒歩",
  cycling: "自転車",
  transit: "電車",
  bus: "バス",
};

const ALL_MODES: TravelMode[] = ["driving", "walking", "cycling", "transit", "bus"];

/**
 * TravelTimeIndicator displays travel time and distance between spots.
 * Shows duration, distance, and mode icon in a compact format.
 * Optionally allows changing the travel mode via a popover.
 */
export function TravelTimeIndicator({
  durationMinutes,
  distanceKm,
  mode,
  isEstimate,
  onModeChange,
  showPlaceholder = false,
}: TravelTimeIndicatorProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Show placeholder if travel info is not available but showPlaceholder is true
  if (durationMinutes == null || mode == null) {
    if (!showPlaceholder) {
      return null;
    }

    // Render placeholder
    return (
      <div className="flex items-center justify-center py-1.5">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground/50">
          <div className="flex flex-col items-center">
            <div className="h-2 w-px bg-border/50" />
            <div className="my-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-dashed bg-background">
              <MoreHorizontal className="h-3 w-3" />
            </div>
            <div className="h-2 w-px bg-border/50" />
          </div>
          <span className="italic">移動時間未計算</span>
        </div>
      </div>
    );
  }

  const icon = MODE_ICONS[mode];
  const colorClass = MODE_COLORS[mode];

  // Format duration
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}分`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}時間${mins}分` : `${hours}時間`;
  };

  const handleModeSelect = (newMode: TravelMode) => {
    setIsOpen(false);
    if (onModeChange && newMode !== mode) {
      onModeChange(newMode);
    }
  };

  const indicator = (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {/* Vertical connector line */}
      <div className="flex flex-col items-center">
        <div className="h-2 w-px bg-border" />
        <div className="my-0.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background">
          <span className={colorClass}>{icon}</span>
        </div>
        <div className="h-2 w-px bg-border" />
      </div>

      {/* Duration and distance */}
      <div className="flex items-center gap-1">
        <span className="font-medium">{formatDuration(durationMinutes)}</span>
        {distanceKm != null && (
          <span className="text-muted-foreground/70">({distanceKm.toFixed(1)}km)</span>
        )}
        {isEstimate && <span className="text-muted-foreground/50">*</span>}
        {onModeChange && <ChevronDown className="h-3 w-3 text-muted-foreground/50" />}
      </div>
    </div>
  );

  // If no onModeChange callback, just render the indicator without interaction
  if (!onModeChange) {
    return <div className="flex items-center justify-center py-1.5">{indicator}</div>;
  }

  return (
    <div className="flex items-center justify-center py-1.5">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="cursor-pointer rounded-md px-2 py-1 transition-colors hover:bg-muted/50"
          >
            {indicator}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="center">
          <div className="flex gap-1">
            {ALL_MODES.map((m) => (
              <Button
                key={m}
                variant={m === mode ? "default" : "ghost"}
                size="sm"
                className="flex h-8 w-8 flex-col items-center justify-center p-0"
                onClick={() => handleModeSelect(m)}
                title={MODE_LABELS[m]}
              >
                <span className={m === mode ? "text-primary-foreground" : MODE_COLORS[m]}>
                  {MODE_ICONS[m]}
                </span>
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
